import Phaser from 'phaser';
import { isCalmMode, CALM_AUDIO_FACTOR, onCalmModeChange } from './CalmMode.js';

/**
 * Background music system.
 *
 * - Tracks are grouped by "context" (e.g. 'arena', 'menu'). Adding new
 *   songs is just registering a key in TRACKS or calling addTracks().
 * - playContext(game, 'arena') picks a random track from that context,
 *   plays it once, and auto-advances to a different random track when
 *   it ends.
 * - Uses the global game SoundManager so playback survives scene
 *   transitions (no audio glitch when a scene starts/stops).
 */

const MUSIC_VOLUME = 0.3;
const STORAGE_KEY = 'snake_music_enabled';

/** Catalog of music keys per context. Add more by editing this map or
 *  calling `addTracks` at runtime. */
const TRACKS: Record<string, string[]> = {
  arena: ['arena_sand1', 'arena_sand2'],
  menu:  ['menu_sand1', 'menu_sand2', 'menu_sand3'],
  tournament: ['musicTournamentpeng1', 'musicTournamentpeng2', 'musicTournamentpeng3'],
};

let currentSound: Phaser.Sound.BaseSound | null = null;
let currentContext: string | null = null;
let currentKey: string | null = null;
let lastGame: Phaser.Game | null = null;
let enabled: boolean = (() => {
  try { return localStorage.getItem(STORAGE_KEY) !== '0'; }
  catch { return true; }
})();
const listeners: Set<(on: boolean) => void> = new Set();

/** Effective playback volume — base track volume, reduced while Calm Mode is on. */
function effectiveMusicVolume(): number {
  return MUSIC_VOLUME * (isCalmMode() ? CALM_AUDIO_FACTOR : 1);
}

/** Re-apply the effective volume to the currently-playing track. Called when
 *  Calm Mode toggles so the change is heard live without cutting the song. */
export function applyCalmToMusic(): void {
  setMusicVolume(effectiveMusicVolume());
}

// Keep the live track's volume in sync with Calm Mode.
onCalmModeChange(applyCalmToMusic);

/** Register additional tracks for a context (extensible — call once on boot). */
export function addTracks(context: string, keys: string[]): void {
  TRACKS[context] = [...(TRACKS[context] ?? []), ...keys];
}

/** Begin playing music for the given context. Idempotent — calling twice
 *  with the same context while playing is a no-op. */
export function playContext(game: Phaser.Game, context: string): void {
  lastGame = game;
  if (currentContext === context && currentSound?.isPlaying) return;
  stopMusic();
  currentContext = context;
  if (!enabled) return;
  playRandomFromCurrentContext(game);
}

function playRandomFromCurrentContext(game: Phaser.Game): void {
  if (!currentContext) return;
  const pool = TRACKS[currentContext] ?? [];
  if (pool.length === 0) return;

  // Only consider tracks that are actually in the audio cache. Tracks
  // get lazy-loaded after BootScene, so during the first ~seconds of a
  // session some entries in `pool` may not be ready yet.
  const loaded = pool.filter(k => game.cache.audio.exists(k));
  if (loaded.length === 0) return;

  // Avoid replaying the same track if more than one is available
  const candidates = loaded.length > 1 ? loaded.filter(k => k !== currentKey) : loaded;
  const key = candidates[Math.floor(Math.random() * candidates.length)];

  const sound = game.sound.add(key, { volume: effectiveMusicVolume(), loop: false });
  sound.once(Phaser.Sound.Events.COMPLETE, () => {
    sound.destroy();
    if (currentSound === sound) {
      currentSound = null;
      // Auto-advance to the next track in the same context
      playRandomFromCurrentContext(game);
    }
  });
  sound.play();
  currentSound = sound;
  currentKey = key;
}

/** Stop the current track immediately. Safe to call when nothing is playing. */
export function stopMusic(): void {
  if (currentSound) {
    currentSound.stop();
    currentSound.destroy();
    currentSound = null;
  }
  currentContext = null;
  currentKey = null;
}

/** Adjust volume of the currently-playing track (0..1). */
export function setMusicVolume(volume: number): void {
  if (currentSound && 'setVolume' in currentSound) {
    (currentSound as Phaser.Sound.WebAudioSound).setVolume(volume);
  }
}

/** Master toggle — wired to the analog music switch in the TV bezel.
 *  Persists to localStorage so the user's choice survives reload. */
export function setMusicEnabled(on: boolean): void {
  if (enabled === on) return;
  enabled = on;
  try { localStorage.setItem(STORAGE_KEY, on ? '1' : '0'); } catch { /* ignore */ }
  for (const cb of listeners) cb(on);
  if (!on) {
    if (currentSound) {
      currentSound.stop();
      currentSound.destroy();
      currentSound = null;
    }
  } else if (currentContext && lastGame) {
    playRandomFromCurrentContext(lastGame);
  }
}

export function isMusicEnabled(): boolean {
  return enabled;
}

/** Subscribe to enabled-state changes (returns an unsubscribe function). */
export function onMusicEnabledChange(cb: (on: boolean) => void): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}
